import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import * as XLSX from 'xlsx';

import { createStyles, makeStyles, Theme, withStyles } from '@material-ui/core/styles';
import PlaylistAddCheckIcon from '@material-ui/icons/PlaylistAddCheck';
import {
  DataGrid, GridColDef, GridRowParams, GridToolbarColumnsButton, GridToolbarContainer,
  GridValueFormatterParams
} from '@mui/x-data-grid';

import Confirm from '../../components/confirm';
import CustomDataGrid from '../../components/data-grid';
import SaveBackdrop from '../../components/saveBackdrop';
import Screen from '../../components/screen';
import SplashWait from '../../components/splash-wait';
import { AppContext } from '../../contexts/app-context';
import GlobalStyles from '../../global-styles';
import API from '../../utils/api';
import {
  apiReponseError, apiRequestError, canReadEnlistment, canWriteEnlistment, checkUserAuthenticated,
  formatDateForDataGrid, getEnlistmentProgramLocations, getShowDashboardValue, goToRoutePage,
  setWindowTitle, toggleShowDashboard
} from '../../utils/helper';
import { DashboardOffIcon, DashboardOnIcon, MoreIcon, RefreshIcon } from '../../utils/icons';
import {
  Box, Breadcrumbs, Button, Card, CardContent, CardHeader, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControl, Grid, Hidden, IconButton, InputLabel, Link, ListItemIcon,
  ListItemText, Menu, MenuItem, Select, TextField, Tooltip, Typography
} from '../../utils/material-ui-core';
import Review from './curd/review';

const options = {
  chart: {
    type: "spline",
    backgroundColor: "transparent",
    height: "100%",
  },
  title: {
    text: "",
  },
  xAxis: {
    title: {
      text: "",
    },
    labels: {
      enabled: false,
    },
    tickInterval: 1,
    type: "datetime",
  },
  yAxis: {
    title: {
      text: "",
    },
    labels: {
      enabled: true,
    },
    tickInterval: 1,
  },
  legend: {
    enabled: false,
  },
  series: [
    {
      name: "Enrollment",
      data: [0, 1, 2],
    },
  ],
};

const StyledMenuItem = withStyles((theme) => ({
  root: {
    "&:focus": {
      backgroundColor: theme.palette.secondary.main,
      "& .MuiListItemIcon-root, & .MuiListItemText-primary": {
        color: theme.palette.common.white,
      },
    },
  },
}))(MenuItem);

const Enlistment = () => {
  const pageStyles = LocalPageStyles();
  const globalStyles = GlobalStyles();

  const { showAlert, isUserAuthenticated, enlistmentPartners, enlistmentStatus, enlistmentPartnerKey, enlistmentProgramKey, setEnlistmentKeys, userFunctionAccess, refreshData } = useContext(AppContext);

  const [reviewEditDialogOpen, setReviewEditDialogOpen] = useState<boolean>(false);
  const [keys, setKeys] = useState({
    partnerKey: "",
    programKey: "",
    locationItemKey: "",
  });

  const [moreAnchorEl, setMoreAnchorEl] = React.useState(null);
  const [batchReviewConfirmDialogOpen, setBatchReviewConfirmDialogOpen] = useState<boolean>(false);
  const [openBatchReviewDialog, setOpenBatchReviewDialog] = useState(false);
  const [batchReviewStatus, setBatchReviewStatus] = useState(1);
  const [batchReviewPageSize, setBatchReviewPageSize] = useState(100);
  const [selectedBatchReviewLocationKeys, setSelectedBatchReviewLocationKeys] = useState<string[]>([]);
  const [saveInProgress, setSaveInProgress] = useState<boolean>(false);
  const [chartOptions, setChartOptions] = useState(options);
  const [loading, setLoading] = useState<boolean>(true);
  const [showDashboard, setShowDashboard] = useState<boolean>(true);
  const [isPageInitialize, setIsPageInitialize] = useState<boolean>(false);
  const [programStatusDashboard, setProgramStatusDashboard] = useState<ProgramStatusDashboard[]>([]);
  const [locations, setLocations] = useState([]);
  const [enlistmentReviewLocations, setEnlistmentReviewLocations] = useState<EnlistmentReviewAccount[]>([]);

  const [enlistmentPendingReviewLocations, setEnlistmentPendingReviewLocations] = useState<EnlistmentReviewAccount[]>([]);
  const [batchEnlistmentReviewLocations, setBatchEnlistmentReviewLocations] = useState<any[]>([]);


  const [reviewLocationSearch, setReviewLocationSearch] = useState<string>("");

  const [openImportReviewDataDialog, setOpenImportReviewDataDialog] = useState(false);
  const [dataImported, setDataImported] = useState(false);
  const [dataImportedFirstTime, setDataImportedFirstTime] = useState(true);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const columns = [
    { field: "statusName", headerName: "Status", width: 200, hide: false },
    { field: "partnerName", headerName: "Company", width: 350, hide: true },
    { field: "programName", headerName: "Program", width: 350, hide: false },
    { field: "accountGroupName", headerName: "Group/Business Name", width: 350, hide: false },
    {
      field: "accountNumber",
      headerName: "Account Number",
      width: 250,
      hide: false,
    },
    {
      field: "accountName",
      headerName: "Account Name",
      width: 350,
      hide: false,
    },
     {
      field: "slimEligible",
      headerName: "Slim Eligible",
      width: 175,
      hide: false,
      valueFormatter: (params: GridValueFormatterParams) => (params.value ? "Yes" : "No"),
    },
    {
      field: "accountAddressLine1",
      headerName: "Account - Address Line 1",
      width: 250,
      hide: true,
    },
    {
      field: "accountAddressLine2",
      headerName: "Account - Address Line 2",
      width: 250,
      hide: true,
    },
    {
      field: "accountAddressLocality1",
      headerName: "Account - Locality 1",
      width: 250,
      hide: true,
    },
    {
      field: "accountAddressRegion1",
      headerName: "Account - Region 1",
      width: 250,
      hide: true,
    },
    {
      field: "accountAddressPostalCode",
      headerName: "Account - Postal Code",
      width: 250,
      hide: true,
    },
    {
      field: "accountAddressCountry",
      headerName: "Account - Country",
      width: 250,
      hide: true,
    },

    { field: "signupKey", headerName: "Signup Key", width: 200, hide: true },
    {
      field: "parentAccountNumber",
      headerName: "Parent Account Number",
      width: 250,
      hide: true,
    },
    {
      field: "parentAccountName",
      headerName: "Parent Name",
      width: 250,
      hide: true,
    },

    { field: "isParent", headerName: "Parent", width: 150, type: "boolean", hide: true },

    { field: "subscriptionBilling", headerName: "Subscription Billing", width: 250, type: "boolean", hide: true },
    { field: "taxExempt", headerName: "Tax Exempt", width: 250, type: "boolean", hide: true },
    { field: "taxExemptFileExists", headerName: "Tax Exempt File Exists", width: 250, type: "boolean", hide: true },

    { field: "kitName", headerName: "Kit", width: 150, hide: true },
    {
      field: "installMethodName",
      headerName: "Install",
      width: 150,
      hide: true,
    },
    { field: "noofAccounts", headerName: "Location #", width: 150, hide: true },

    {
      field: "contactFirstName",
      headerName: "Contact - First Name",
      width: 250,
      hide: true,
    },
    {
      field: "contactLastName",
      headerName: "Contact - Last Name",
      width: 250,
      hide: true,
    },
    {
      field: "contactPhone",
      headerName: "Contact - Phone",
      width: 250,
      hide: true,
    },
    {
      field: "contactEmail",
      headerName: "Contact - Email",
      width: 250,
      hide: true,
    },

    {
      field: "shippingOrganizationName",
      headerName: "Ship To - Organization Name",
      width: 250,
      hide: true,
    },
    {
      field: "shippingFirstName",
      headerName: "Ship To - First Name",
      width: 250,
      hide: true,
    },
    {
      field: "shippingLastName",
      headerName: "Ship To - Last Name",
      width: 250,
      hide: true,
    },
    {
      field: "shippingPhone",
      headerName: "Ship To - Phone",
      width: 250,
      hide: true,
    },
    {
      field: "shippingEmail",
      headerName: "Ship To - Email",
      width: 250,
      hide: true,
    },
    {
      field: "shippingAddressLine1",
      headerName: "Ship To - Address Line 1",
      width: 250,
      hide: true,
    },
    {
      field: "shippingAddressLine2",
      headerName: "Ship To - Address Line 2",
      width: 250,
      hide: true,
    },
    {
      field: "shippingAddressLocality1",
      headerName: "Ship To - Locality 1",
      width: 250,
      hide: true,
    },
    {
      field: "shippingAddressRegion1",
      headerName: "Ship To - Region 1",
      width: 250,
      hide: true,
    },
    {
      field: "shippingAddressPostalCode",
      headerName: "Ship To - Postal Code",
      width: 250,
      hide: true,
    },
    {
      field: "shippingAddressCountry",
      headerName: "Ship To - Country",
      width: 250,
      hide: true,
    },

    {
      field: "errorMessage",
      headerName: "Error Message",
      width: 250,
      hide: false,
    },
    {
      field: "reason",
      headerName: "Reason",
      width: 250,
      hide: true,
    },
    {
      field: "withdrawDate",
      headerName: "Withdraw -  Date",
      width: 200,
      type: "dateTime",
      hide: true,
      valueFormatter: (params: GridValueFormatterParams) => formatDateForDataGrid(params.value as string),
    },
    {
      field: "orderNumber",
      headerName: "Order - Number",
      width: 250,
      hide: true,
    },
    {
      field: "orderPaymentDate",
      headerName: "Order - Payment Date",
      width: 200,
      type: "dateTime",
      hide: true,
      valueFormatter: (params: GridValueFormatterParams) => formatDateForDataGrid(params.value as string),
    },
    {
      field: "cancelOrderDate",
      headerName: "Order Cancel - Date",
      width: 200,
      type: "dateTime",
      hide: true,
      valueFormatter: (params: GridValueFormatterParams) => formatDateForDataGrid(params.value as string),
    },
    {
      field: "cancelOrderNote",
      headerName: "Order Cancel - Note",
      width: 250,
      hide: true,
    },
    {
      field: "cancelOrderRefundStatus",
      headerName: "Order Cancel - Refunded",
      width: 150,
      type: "boolean",
      hide: true,
    },
    {
      field: "cancelOrderRefundAmount",
      headerName: "Order Cancel - Refund Amount",
      width: 150,
      hide: true,
    },
    {
      field: "cancelOrderRefundDate",
      headerName: "Order Cancel - Refund Date",
      width: 200,
      type: "dateTime",
      hide: true,
      valueFormatter: (params: GridValueFormatterParams) => formatDateForDataGrid(params.value as string),
    },
    {
      field: "registerDateStatus",
      headerName: "Registered",
      width: 150,
      hide: false, sortable: false
    },
    {
      field: "lastModifiedBy",
      headerName: "Last Modified By",
      width: 250,
      hide: true,
    },
    {
      field: "lastModifiedDateTime",
      headerName: "Last Modified Date",
      width: 200,
      type: "dateTime",
      hide: true,
      valueFormatter: (params: GridValueFormatterParams) => formatDateForDataGrid(params.value as string),
    },
    { field: "locationItemKey", headerName: "Key", width: 350, hide: true },
    {
      field: "reviewCompleted",
      headerName: "Review Completed Date",
      width: 200,
      type: "dateTime",
      hide: true,
      valueFormatter: (params: GridValueFormatterParams) => formatDateForDataGrid(params.value as string),
    },
    {
      field: "enrollCompleted",
      headerName: "Enrolled Date",
      width: 200,
      type: "dateTime",
      hide: true,
      valueFormatter: (params: GridValueFormatterParams) => formatDateForDataGrid(params.value as string),
    },
   
  ];

  // Batch Location Review Table
  const columnsForBatchReview: GridColDef[] = [
    { field: 'locationItemKey', headerName: 'Key', width: 330 },
    { field: 'accountNumber', headerName: 'Account Number', width: 250 },
    { field: 'accountName', headerName: 'Account Name', width: 250 },
    { field: 'address', headerName: 'Address', width: 350 },
    { field: 'kitNo', headerName: 'Kit No', width: 100 },
    { field: 'kitName', headerName: 'Kit Name', width: 180 },
    { field: 'slimEligible', headerName: 'Slim Eligible', width: 150, hide: false, valueFormatter: (params) => (params.value ? "Yes" : "No")},
    { field: 'statusName', headerName: 'Status', width: 140 },
    { field: 'registered', headerName: 'Register', width: 200 },
    { field: 'errorMessage', headerName: 'Error', width: 400 },
    { field: 'contactName', headerName: 'Contact Name', width: 250 },
    { field: 'contactPhone', headerName: 'Contact Phone', width: 200 },
    { field: 'contactEmail', headerName: 'Contact Email', width: 200 },
    { field: 'shippingAddress', headerName: 'Shipping Address', width: 350 },
    { field: 'shippingContactName', headerName: 'Shipping Contact Name', width: 250 },
    { field: 'shippingContactPhone', headerName: 'Shipping Contact Phone', width: 200 },
    { field: 'shippingContactEmail', headerName: 'Shipping Contact Email', width: 200 },
    { field: 'partnerName', headerName: 'Partner Name', width: 250 },
    { field: 'programName', headerName: 'Program Name', width: 250 },
    { field: 'programCode', headerName: 'Program Code', width: 180 },

  ];

  // Handle file export
  const handleReviewFileExport = () => {

    const exportRows = batchEnlistmentReviewLocations.map(({ id, ...rest }) => rest);

    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'data');
    const filename = `enlistment_${uuidv4()}.xlsx`;
    XLSX.writeFile(workbook, filename);
  }

  // Handle file import
  const handleReviewFileImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      setOpenImportReviewDataDialog(false);
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target!.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });

        const sheetName = 'data';
        if (!workbook.SheetNames.includes(sheetName)) {
          showAlert({ message: `Sheet named "${sheetName}" not found.`, severity: "error" });
          return;
        }

        const sheet = workbook.Sheets[sheetName];
        const json = XLSX.utils.sheet_to_json<Record<string, any>>(sheet, { defval: "" });

        if (json.length === 0) {
          showAlert({ message: 'Sheet "Data" is empty.', severity: "error" });
          return;
        }

        const firstRow = json[0];
        const normalizedKeys = Object.keys(firstRow).map(k => k.toLowerCase());

        if (!normalizedKeys.includes('locationitemkey') || !normalizedKeys.includes('accountnumber')) {
          showAlert({ message: 'Columns "key" and "accountnumber" must exist in the sheet.', severity: "error" });
          return;
        }

        const keyValuesDelimited = Array.from(
          new Set(
            json
              .map(row => {
                const entry = Object.entries(row).find(([k]) => k.toLowerCase() === 'locationitemkey');
                return entry ? String(entry[1]).trim() : null;
              })
              .filter(Boolean)))
          .sort()
          .join('|');


        const accountNumberMap = new Map(
          json
            .map(row => {
              // Find actual property names ignoring case
              const keyProp = Object.keys(row).find(k => k.toLowerCase() === 'locationitemkey');
              const accountNumProp = Object.keys(row).find(k => k.toLowerCase() === 'accountnumber');

              if (batchReviewStatus === 0 && keyProp && row[keyProp]) {
                return [String(row[keyProp]).trim(), ""];
              }
              else if (batchReviewStatus === 1 && keyProp && accountNumProp && row[keyProp] && row[accountNumProp]) {
                return [String(row[keyProp]).trim(), String(row[accountNumProp]).trim()];
              }

              return null;
            })
            .filter((item): item is [string, string] => item !== null)
        );

        setSelectedBatchReviewLocationKeys(Array.from(accountNumberMap.keys()));

        let keyExists = false;


        const tempBatchEnlistmentReviewLocations = enlistmentPendingReviewLocations.map(acc => {
          const locationKeyLower = acc.locationItemKey.trim();
          let newAccountNumber = accountNumberMap.get(locationKeyLower);

          if (newAccountNumber) {
            keyExists = true;
          }

          // Force empty string if null or undefined
          newAccountNumber = newAccountNumber ?? "";

          return {
            ...acc,
            account: {
              ...acc.account,
              accountNumber: newAccountNumber ?? acc.account.accountNumber,
            }
          };
        });

        setEnlistmentPendingReviewLocations(tempBatchEnlistmentReviewLocations);

        setLoading(true);
        if (!keyExists) {
          setLoading(false);
          showAlert({ message: 'No matching keys found in the file.', severity: "info" });
        }
        else {
          showAlert({ message: 'Mapping keys...', severity: "info" });
          setDataImportedFirstTime(false);
          setDataImported(true);
          setTimeout(() => {
            setReviewLocationSearch(keyValuesDelimited);
            filterBatchReviewLocations(keyValuesDelimited, tempBatchEnlistmentReviewLocations);
            setLoading(false);
          }, 2000);
          setBatchReviewStatus(1)
        }

      } catch (error) {
        showAlert({ message: 'Failed to read the file. Please upload a valid XLSX file.', severity: "error" });
      }
    };
    reader.readAsArrayBuffer(file);

    setOpenImportReviewDataDialog(false);
  };

  // Handle file import cancel
  const handleReviewFileImportCancel = async () => {
    setLoading(true);
    setDataImported(false);
    setDataImportedFirstTime(true);
    setSelectedBatchReviewLocationKeys([]);
    setReviewLocationSearch("");
    await getPendingReviewLocations();
    setLoading(false);
  }

  const CustomToolbar = () => {
    return (
      <GridToolbarContainer>
        <GridToolbarColumnsButton />
      </GridToolbarContainer>
    );
  }

  // Handle file import dialog open
  const showBatchReview = (data) => {
    setLoading(true);

    filterBatchReviewLocations("", data);
    setReviewLocationSearch("");
    setDataImportedFirstTime(true);
    setDataImported(false);
    setSelectedBatchReviewLocationKeys([]);
    setLoading(false);
    setOpenBatchReviewDialog(true);
  }

  // Handle file import dialog close
  const closeBatchReview = () => {
    setBatchEnlistmentReviewLocations([]);
    setSelectedBatchReviewLocationKeys([]);
    filterBatchReviewLocations("", []);
    setReviewLocationSearch("");
    setOpenBatchReviewDialog(false);
  }

  // Get enlistment batch review locations karthi
  const getEnlistmentBatchReviewLocationCount = () => {
    return enlistmentPendingReviewLocations.filter(a => {
      const { status, isError, errorMessage } = a.reviewLocationOtherAttributes;

      const isValidNormal = [1, 3].includes(status) && !isError;

      const isAccountNumberError =
        isError &&
        typeof errorMessage === 'string' &&
        errorMessage.toLowerCase().includes('account number') &&
        (errorMessage.match(/\./g) || []).length === 1;

      return isValidNormal || isAccountNumberError;
    }).length;
  };

  const filterBatchReviewLocations = (search: string, data: any[]) => {
    const terms = Array.from(
      new Set(
        search
          .split('|')
          .map(t => t.trim().toLowerCase())
          .filter(t => t.length > 0)
      )
    );

    const shouldFilter = terms.length > 0;

    const filtered = data.filter(a => {

      const { status, isError, errorMessage } = a.reviewLocationOtherAttributes;
      const isValidNormal = [1, 3].includes(status) && !isError;
      const isAccountNumberError =
        isError &&
        typeof errorMessage === 'string' &&
        errorMessage.toLowerCase().includes('account number') &&
        (errorMessage.match(/\./g) || []).length === 1;


      if (!(isValidNormal || isAccountNumberError)) return false;

      if (a.reviewLocationOtherAttributes.noofAccounts === 11) return false;

      if (!a.reviewLocationOtherAttributes.externalValidationStatus) return false;

      if (!shouldFilter) return true;

      const allFields = [
        a.locationItemKey,
        a.account?.accountName,
        a.account?.accountNumber,
        a.reviewLocationOtherAttributes?.partnerName,
        a.reviewLocationOtherAttributes?.programName,
        a.reviewLocationOtherAttributes?.kitName,
        a.reviewLocationOtherAttributes?.statusName,
        a.kitNo?.toString(),
        a.programCode?.toString(),
        [
          a.account.address?.addressline1,
          a.account.address?.addressline2,
          a.account.address?.addressline3,
          a.account.address?.locality1,
          a.account.address?.region1,
          a.account.address?.postalCode,
          a.account?.address?.country
        ].filter(Boolean).join(', '),
        a.account?.address?.addressline1,
        a.account?.address?.addressline2,
        a.account?.address?.addressline3,
        a.account?.address?.locality1,
        a.account?.address?.locality2,
        a.account?.address?.region1,
        a.account?.address?.region2,
        a.account?.address?.country,
        a.account?.address?.postalCode,
        [
          a.shipping?.address?.addressline1,
          a.shipping?.address?.addressline2,
          a.shipping?.address?.addressline3,
          a.shipping?.address?.locality1,
          a.shipping?.address?.locality2,
          a.shipping?.address?.region1,
          a.shipping?.address?.region2,
          a.shipping?.address?.postalCode,
          a.shipping?.address?.country,
        ].filter(Boolean).join(', '),
        a.shipping?.address?.addressline1,
        a.shipping?.address?.addressline2,
        a.shipping?.address?.addressline3,
        a.shipping?.address?.locality1,
        a.shipping?.address?.locality2,
        a.shipping?.address?.region1,
        a.shipping?.address?.region2,
        a.shipping?.address?.country,
        a.shipping?.address?.postalCode,
        a.shipping?.firstName,
        a.shipping?.lastName,
        a.shipping?.email,
        a.shipping?.phone,
        a.contact?.firstName,
        a.contact?.lastName,
        a.contact?.email,
        a.contact?.phone,
        a.reviewLocationOtherAttributes?.registerDateStatus,
        a.reviewLocationOtherAttributes?.errorMessage,
        a.slimEligible
      ]
        .filter(Boolean)
        .map(f => f.toString().toLowerCase());

      return terms.some(term => allFields.some(field => field.includes(term)));
    });


    // loads data to the Batch Location Review table

    const rows = filtered.map(acc => ({
      id: acc.locationItemKey,
      locationItemKey: acc.locationItemKey,
      accountNumber: acc.account.accountNumber,
      accountName: acc.account.accountName,
      address: [
        acc.account.address?.addressline1,
        acc.account.address?.addressline2,
        acc.account.address?.addressline3,
        acc.account.address?.locality1,
        acc.account.address?.region1,
        acc.account.address?.postalCode,
      ].filter(Boolean).join(', '),

      kitNo: acc.kitNo,
      kitName: acc.reviewLocationOtherAttributes.kitName,
      statusName: acc.reviewLocationOtherAttributes.statusName,

      slimEligible: acc.slimEligible,

      contactName: `${acc.contact?.firstName ?? ''} ${acc.contact?.lastName ?? ''}`.trim(),
      contactPhone: acc.contact?.phone ?? '',
      contactEmail: acc.contact?.email ?? '',

      shippingAddress: [
        acc.shipping?.address?.addressline1,
        acc.shipping?.address?.addressline2,
        acc.shipping?.address?.addressline3,
        acc.shipping?.address?.locality1,
        acc.shipping?.address?.region1,
        acc.shipping?.address?.postalCode,
      ].filter(Boolean).join(', '),

      shippingContactName: `${acc.shipping?.firstName ?? ''} ${acc.shipping?.lastName ?? ''}`.trim(),
      shippingContactPhone: acc.shipping?.phone ?? '',
      shippingContactEmail: acc.shipping?.email ?? '',

      partnerName: acc.reviewLocationOtherAttributes.partnerName,
      programName: acc.reviewLocationOtherAttributes.programName,
      programCode: acc.programCode,
      registered: acc.reviewLocationOtherAttributes?.registerDateStatus,
      errorMessage: acc.reviewLocationOtherAttributes?.errorMessage.trim(),
      isError: acc.reviewLocationOtherAttributes?.isError,
      status: acc.reviewLocationOtherAttributes?.status
    }));

    setBatchEnlistmentReviewLocations(rows);
    return rows;
  };

  // Handle more menu click
  const handleMoreMenuClick = (event) => {
    setMoreAnchorEl(event.currentTarget);
  };

  // Handle more menu close
  const handleMoreMenuClose = () => {
    setMoreAnchorEl(null);
  };

  // Batch review confirmation
  const batchReviewConfirmation = async (confirm: boolean) => {
    setBatchReviewConfirmDialogOpen(false);
    if (confirm) {
      setOpenBatchReviewDialog(false);
      setSaveInProgress(true);
      setTimeout(async () => { await saveBatchReviewLocations(); }, 1000);

    }
  };

  // Review new/edit dialog result
  const reviewDialogResult = (confirmOutput: ConfirmOutput) => {
    setReviewEditDialogOpen(false);
    if (confirmOutput.status === "refresh") {
      refreshReviewLocations();
      refreshData("enlistmentpartners");
    }
  };

  // Handle datagrid click
  const handleDataGridClick = (params: GridRowParams, event: any) => {
    if (enlistmentReviewLocations != null && enlistmentReviewLocations.length > 0) {
      const filterLocations = enlistmentReviewLocations.filter((enlistmentReviewAccount) => enlistmentReviewAccount.locationItemKey === params.row.locationItemKey);
      if (filterLocations.length) {
        const tempKeys = {
          partnerKey: filterLocations[0].reviewLocationOtherAttributes.partnerKey,
          programKey: filterLocations[0].reviewLocationOtherAttributes.programKey,
          locationItemKey: params.row.locationItemKey,
        };
        setKeys(tempKeys);
        setReviewEditDialogOpen(true);
      }
    }
  };

  // Handle partner key change
  const handlePartnerKeyChange = async (key: string) => {
    if (key !== "") {
      const filterParents = enlistmentPartners.filter((p) => p.partnerKey === key);
      if (filterParents.length > 0) {
        if (filterParents[0].programs.length > 0) {
          setEnlistmentKeys(key, filterParents[0].programs[0].programKey, "pending");
          await getReviewLocations(key, filterParents[0].programs[0].programKey, "pending");
        }
      }
    }
  };

  // Handle program key change
  const handleProgramKeyChange = async (key: string) => {
    if (enlistmentPartnerKey !== "") {
      setEnlistmentKeys(enlistmentPartnerKey, key, enlistmentStatus);
      await getReviewLocations(enlistmentPartnerKey, key, enlistmentStatus);
    }
  };

  // Handle status change
  const handleStatusChange = async (status: string) => {
    if (enlistmentPartnerKey !== "") {
      setEnlistmentKeys(enlistmentPartnerKey, enlistmentProgramKey, status);
      await getReviewLocations(enlistmentPartnerKey, enlistmentProgramKey, status);
    }
  };

  // Handle dashboard status link click
  const handleDashboardStatusLinkClicked = async (status: string) => {

    if (status === "Pilot") {
      return;
    }

    if (enlistmentPartnerKey !== "" && enlistmentProgramKey !== "") {
      setEnlistmentKeys(enlistmentPartnerKey, enlistmentProgramKey, status.replaceAll(" ", "").toLowerCase());
      await getReviewLocations(enlistmentPartnerKey, enlistmentProgramKey, status.replaceAll(" ", ""));
    }
  };

  // Refresh review locations
  const refreshReviewLocations = async () => {
    await getReviewLocations(enlistmentPartnerKey, enlistmentProgramKey, enlistmentStatus);
  };

  // Validate batch review locations
  const validateBatchReviewLocations = () => {

    try {
      const locations = batchEnlistmentReviewLocations
        .filter(item => selectedBatchReviewLocationKeys.includes(String(item.locationItemKey)))
        .map(item => ({
          locationItemKey: item.locationItemKey,
          accountNumber: item.accountNumber
        }));

      if (locations.length === 0) {
        showAlert({ message: "No location selected.", severity: "error" });
        return false;
      }

      if (batchReviewStatus === 1) {
        // Check for duplicate account numbers
        const accountCount = new Map<string, number>();
        const emptyAccounts: string[] = [];

        locations.forEach(loc => {
          const accNum = loc.accountNumber?.trim();
          if (!accNum) {
            emptyAccounts.push('(empty)');
            return;
          }
          accountCount.set(loc.accountNumber, (accountCount.get(loc.accountNumber) || 0) + 1);
        });

        if (emptyAccounts.length > 0) {
          showAlert({ message: `Empty account number(s) found.`, severity: "error" });
          return false;
        }

        const duplicateAccounts = Array.from(accountCount.entries())
          .filter(([_, count]) => count > 1)
          .map(([accountNumber]) => accountNumber);

        if (duplicateAccounts.length > 0) {
          showAlert({
            message: `Duplicate account numbers found: ${duplicateAccounts.join(', ')}`,
            severity: "error"
          });
          return false;
        }

      }
      return true;

    }
    catch { }

    showAlert({
      message: `Failed to validate batch review locations.`,
      severity: "error"
    });
    return false;
  }

  // Save batch reviewed locations
  const saveBatchReviewLocations = async () => {

    try {



      if (!validateBatchReviewLocations()) {
        setOpenBatchReviewDialog(true);
        setSaveInProgress(false);
        return;
      }

      const locations = batchEnlistmentReviewLocations
        .filter(item => selectedBatchReviewLocationKeys.includes(String(item.locationItemKey)))
        .map(item => ({
          locationItemKey: item.locationItemKey,
          accountNumber: item.accountNumber
        }));

      const payload = {
        status: batchReviewStatus,
        locations: locations,
      }

      await API.post("/api/enlistmentadmin/locations/batch/save", payload)
        .then(async (response) => {
          showAlert({ message: "Successfully saved.", severity: "success" });
          closeBatchReview();
          await refreshReviewLocations();
          setSaveInProgress(false);
          closeBatchReview();
          return;
        })
        .catch((error) => {
          setOpenBatchReviewDialog(true);
          setSaveInProgress(false);
          showAlert({
            message: apiReponseError(error, "Failed to save location item on hold."),
            severity: "error",
          });
        });
    } catch (e) {
      setOpenBatchReviewDialog(true);
      setSaveInProgress(false);
      showAlert({
        message: apiRequestError(e, "Something went wrong in our system."),
        severity: "error",
      });
    }



  }

  // Get pending review locations
  const getPendingReviewLocations = async () => {
    setLoading(true);
    const apiResponse = await getEnlistmentProgramLocations(enlistmentPartnerKey, enlistmentProgramKey, "pending");

    if (apiResponse.success) {
      const data = apiResponse.data;
      setEnlistmentPendingReviewLocations(data.locations);
      showBatchReview(data.locations)
      setTimeout(() => { setLoading(false); }, data.locations.length > 500 ? 3000 : 1000)
    }
    else {
      setLoading(false);
      showAlert({ message: apiResponse.errorMessage, severity: "error" });
      return;
    }

  }


  // Get review locations
  const getReviewLocations = async (partnerKey: string, programKey: string, status: string) => {
    setLoading(true);
    setLocations([]);

    const apiResponse = await getEnlistmentProgramLocations(partnerKey, programKey, status);

    if (apiResponse.success) {
      const data = apiResponse.data;

      setEnlistmentReviewLocations(data.locations);
      setProgramStatusDashboard(data.programStatusDashboard);

      options.series[0].data = [];

      try {
        const chartData = [] as any;
        const tempProgramEnrollmentDashboards = data.programEnrollmentDashboard as ProgramEnrollmentDashboard[];
        if (tempProgramEnrollmentDashboards.length > 0) {
          tempProgramEnrollmentDashboards.map((item) => chartData.push([Date.UTC(item.year, item.month, item.day), item.count]));
        }
        options.series[0].data = chartData;
      } catch (e) { }

      setChartOptions(options);

      const rows: any = [];
      data.locations.map((location, index) =>
        rows.push({
          id: index,
          locationItemKey: location.locationItemKey,
          parentAccountNumber: location.parentAccountNumber,
          signupKey: location.signupKey,
          lastModifiedBy: location.lastModifiedBy,
          lastModifiedDateTime: location.lastModifiedDateTime,
          
          slimEligible: location.slimEligible,

          accountNumber: location.account.accountNumber,
          accountName: location.account.accountName,
          accountAddressLine1: location.account.address.addressline1,
          accountAddressLine2: location.account.address.addressline2,
          accountAddressLocality1: location.account.address.locality1,
          accountAddressRegion1: location.account.address.region1,
          accountAddressPostalCode: location.account.address.postalCode,
          accountAddressCountry: location.account.address.country,

          contactFirstName: location.contact.firstName,
          contactLastName: location.contact.lastName,
          contactPhone: location.contact.phone,
          contactEmail: location.contact.email,

          shippingOrganizationName: location.shipping.organizationName,
          shippingFirstName: location.shipping.firstName,
          shippingLastName: location.shipping.lastName,
          shippingPhone: location.shipping.phone,
          shippingEmail: location.shipping.email,
          shippingAddressLine1: location.shipping.address.addressline1,
          shippingAddressLine2: location.shipping.address.addressline2,
          shippingAddressLocality1: location.shipping.address.locality1,
          shippingAddressRegion1: location.shipping.address.region1,
          shippingAddressPostalCode: location.shipping.address.postalCode,
          shippingAddressCountry: location.shipping.address.country,
          ...location.reviewLocationOtherAttributes,
        })
      );

      setLocations(rows);

      setLoading(false);
    } else {
      setLoading(false);
      showAlert({ message: apiResponse.errorMessage, severity: "error" });
      return;
    }
  };

  useEffect(() => {
    setWindowTitle("Enlistment");
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const initialPageAsync = async () => {
      if (isPageInitialize) {
        return;
      }
      checkUserAuthenticated(isUserAuthenticated);

      if (!isUserAuthenticated) {
        goToRoutePage("home");
        return;
      }

      if (!canReadEnlistment(userFunctionAccess)) {
        showAlert({ message: "Access Denied.", severity: "error" });
        goToRoutePage("home");
        return;
      }
      if (enlistmentPartners.length === 0) {
        setIsPageInitialize(true);
        setLoading(false);
        return;
      }

      setShowDashboard(getShowDashboardValue("ShowEnlistmentDashboard", true));

      setIsPageInitialize(true);
      setLoading(false);

      if (enlistmentPartnerKey === "" || enlistmentProgramKey === "") {
        if (enlistmentPartnerKey !== "") {
          handlePartnerKeyChange(enlistmentPartnerKey);
        } else {
          if (enlistmentPartners.length > 0) {
            let filterEnlistmentPartners = enlistmentPartners.filter((enlistmentPartner) => enlistmentPartner.pendingCount > 0);
            if (filterEnlistmentPartners.length > 0) {
              handlePartnerKeyChange(filterEnlistmentPartners[0].partnerKey);
            } else {
              filterEnlistmentPartners = enlistmentPartners.filter((enlistmentPartner) => enlistmentPartner.pendingCount === 0);
              if (filterEnlistmentPartners.length > 0) {
                const tempPartnerKey = filterEnlistmentPartners[0].partnerKey;
                if (tempPartnerKey === "") {
                  setTimeout(() => {
                    goToRoutePage("home");
                  }, 300);
                  return;
                }

                handlePartnerKeyChange(tempPartnerKey);
              }
            }
          }
        }
      } else {
        await refreshReviewLocations();
      }
    };

    initialPageAsync();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserAuthenticated, enlistmentPartners]);

  return (
    <React.Fragment>
      {loading ? (
        <SplashWait />
      ) : (
        <Screen>
          <SaveBackdrop open={saveInProgress} />
          <Review open={reviewEditDialogOpen} keys={keys} onCallback={reviewDialogResult} />

          <Confirm title="Confirmation"
            content={`<p>You are ${batchReviewStatus === 1 ? '<b>approving</b> to enroll' : 'withdrawing '} ${selectedBatchReviewLocationKeys.length} location${selectedBatchReviewLocationKeys.length !== 1 ? 's' : ''}.<p/><p>Are you sure you want to continue?</p>`}
            yesText="CONFIRM"
            noText="CANCEL"
            open={batchReviewConfirmDialogOpen}
            callbackOnClose={() => setBatchReviewConfirmDialogOpen}
            callbackOnConfirm={batchReviewConfirmation} />

          <Grid container spacing={3}>
            <Grid item xs={8}>
              <Box className={globalStyles.bottomSpacer}>
                <Breadcrumbs aria-label="breadcrumb">
                  <Link color="inherit" onClick={() => goToRoutePage("home")} className={globalStyles.link}>
                    Home
                  </Link>
                  <Typography color="textPrimary">Enlistment Reviews</Typography>
                </Breadcrumbs>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Hidden xsDown>
                <Box display="flex" justifyContent="flex-end" width={1}>
                  <Box>
                    <IconButton onClick={() => setShowDashboard(toggleShowDashboard("ShowEnlistmentDashboard", showDashboard))}>
                      <Tooltip arrow title={!showDashboard ? "Show Dashboard" : "Hide Dashboard"}>
                        {!showDashboard ? <DashboardOffIcon color="secondary" /> : <DashboardOnIcon color="secondary" />}
                      </Tooltip>
                    </IconButton>
                    {
                      (enlistmentStatus === "pending" || enlistmentStatus === "new" || enlistmentStatus === "review") && locations.length > 0 &&
                      canWriteEnlistment(userFunctionAccess) &&
                      <IconButton>
                        <Tooltip arrow title={"Batch Location Review"}>
                          <PlaylistAddCheckIcon color="secondary" onClick={() => getPendingReviewLocations()} />
                        </Tooltip>
                      </IconButton>
                    }
                    <IconButton>
                      <Tooltip arrow title={"Refresh"}>
                        <RefreshIcon color="secondary" onClick={() => refreshReviewLocations()} />
                      </Tooltip>
                    </IconButton>
                  </Box>
                </Box>
              </Hidden>

              <Hidden smUp>
                <Box display="flex" justifyContent="flex-end" width={1}>
                  <Tooltip arrow title="Menu">
                    <Button color="secondary" aria-controls="fade-menu" aria-haspopup="true" onClick={handleMoreMenuClick}>
                      <MoreIcon />
                    </Button>
                  </Tooltip>
                </Box>

                <Menu elevation={2} id="grid-menu" anchorEl={moreAnchorEl} keepMounted open={Boolean(moreAnchorEl)} onClose={handleMoreMenuClose}>
                  <StyledMenuItem
                    onClick={(e) => {
                      setShowDashboard(toggleShowDashboard("ShowEnlistmentDashboard", showDashboard))
                      handleMoreMenuClose();
                    }}
                  >
                    <ListItemIcon>
                      {!showDashboard ? <DashboardOffIcon fontSize="small" /> : <DashboardOnIcon fontSize="small" />}
                    </ListItemIcon>
                    <ListItemText primary={!showDashboard ? "Show Dashboard" : "Hide Dashboard"} />
                  </StyledMenuItem>
                  {
                    (enlistmentStatus === "pending" || enlistmentStatus === "new" || enlistmentStatus === "review") && locations.length > 0 &&
                    canWriteEnlistment(userFunctionAccess) &&
                    <StyledMenuItem
                      onClick={(e) => {
                        getPendingReviewLocations();
                        handleMoreMenuClose();
                      }}
                    >
                      <ListItemIcon>
                        <PlaylistAddCheckIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={"Batch Location Review"} />
                    </StyledMenuItem>
                  }

                  <StyledMenuItem
                    onClick={(e) => {
                      refreshReviewLocations();
                      handleMoreMenuClose();
                    }}
                  >
                    <ListItemIcon>
                      <RefreshIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={"Refresh "} />
                  </StyledMenuItem>
                </Menu>
              </Hidden>
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            <Grid item xs={12}></Grid>
            <Grid item xs={12} sm={12} md={showDashboard ? 8 : 12} lg={showDashboard ? 9 : 12}>
              <Grid container spacing={3} className={globalStyles.formikContainer}>
                <Grid item xs={12} sm={6} md={4} lg={5}>
                  <FormControl className={globalStyles.formControl} fullWidth={true}>
                    <InputLabel shrink={true}>Company</InputLabel>
                    <Select
                      required
                      native
                      labelId="partner-key-label"
                      id="enlistmentPartnerKey"
                      name="enlistmentPartnerKey"
                      value={enlistmentPartnerKey}
                      onChange={(e) => {
                        handlePartnerKeyChange(e.target.value as string);
                      }}
                      label="Company"
                    >
                      <optgroup label="Review Pending">
                        {enlistmentPartners
                          .filter((enlistmentPartner) => enlistmentPartner.pendingCount > 0)
                          .map((enlistmentPartner, index) => (
                            <option value={enlistmentPartner.partnerKey} key={index}>
                              {enlistmentPartner.name}
                            </option>
                          ))}
                      </optgroup>

                      <optgroup label="Completed">
                        {enlistmentPartners
                          .filter((enlistmentPartner) => enlistmentPartner.pendingCount === 0)
                          .map((enlistmentPartner, index) => (
                            <option value={enlistmentPartner.partnerKey} key={index}>
                              {enlistmentPartner.name}
                            </option>
                          ))}
                      </optgroup>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={5}>
                  <FormControl className={globalStyles.formControl} fullWidth={true}>
                    <InputLabel shrink={true}>Program</InputLabel>
                    <Select
                      required
                      native
                      labelId="program-key-label"
                      id="enlistmentProgramKey"
                      name="enlistmentProgramKey"
                      value={enlistmentProgramKey}
                      onChange={(e) => {
                        handleProgramKeyChange(e.target.value as string);
                      }}
                      label="Program"
                    >
                      {enlistmentPartners
                        .find((item) => item.partnerKey === enlistmentPartnerKey)
                        ?.programs.map((enlistmentProgram, index) => (
                          <option value={enlistmentProgram.programKey} key={index}>
                            {enlistmentProgram.name}
                          </option>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2}>
                  <FormControl className={globalStyles.formControl} fullWidth={true}>
                    <InputLabel shrink={true}>Status</InputLabel>
                    <Select
                      required
                      native
                      labelId="status-label"
                      id="enlistmentStatus"
                      name="enlistmentStatus"
                      value={enlistmentStatus}
                      onChange={(e) => {
                        handleStatusChange(e.target.value as string);
                      }}
                      label="Status"
                    >
                      {enlistmentPartners
                        .find((item) => item.partnerKey === enlistmentPartnerKey)
                        ?.programs.find((item) => item.programKey === enlistmentProgramKey)
                        ?.statusLookupValues.map((statusLookupValue, index) => (
                          <option value={statusLookupValue.value} key={index}>
                            {statusLookupValue.name}
                          </option>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Box p={3}></Box>

              <Box className={pageStyles.datagridContainer}>
                <CustomDataGrid uniqueGridCode={"EnlistmentReviewLocationListResult"} columns={columns} rows={locations} showToolBar={true} sortModel={[{ field: "lastModifiedDateTime", sort: "desc" }]} handleDataGridClick={handleDataGridClick} />
              </Box>
            </Grid>
            {
              showDashboard ?
                <Grid item xs={12} sm={12} md={4} lg={3}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={6} lg={12}>
                      <Card elevation={0} className={globalStyles.rightCardContainer}>
                        <CardHeader title="" subheader="STATUS SUMMARY" />
                        <CardContent style={{ height: "300px", overflow: "auto" }}>
                          <Grid container spacing={3}>
                            {programStatusDashboard.map((item, index) => (
                              <Grid item xs={6} key={index}>

                                <Box display="flex" flexDirection="column" justifyItems='center' alignItems='center' textAlign="center"
                                  onClick={() => { handleDashboardStatusLinkClicked(item.title) }}
                                >
                                  <Box>
                                    <Typography variant="h6" className="count"  >
                                      {item.count}
                                    </Typography>
                                  </Box>
                                  <Box>
                                    <Typography variant="caption" className={globalStyles.toggleFontColor}>

                                      {
                                        item.title !== "Pilot" ? <u>{item.title}</u> : item.title
                                      }
                                    </Typography>
                                  </Box>
                                </Box>
                              </Grid>
                            ))}
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={6} lg={12}>
                      <Card elevation={0} className={globalStyles.rightCardContainer}>
                        <CardHeader title="" subheader="COMPANY SUMMARY" />
                        <CardContent style={{ height: "300px", overflow: "auto" }}>
                          <Grid container spacing={3}>
                            {enlistmentPartners
                              .filter((partner) => partner.pendingCount > 0)
                              ?.map((partner, index) => (
                                <Grid item xs={6} key={index}>
                                  <Box display="flex" flexDirection="column" justifyItems='center' alignItems='center' textAlign="center"
                                    onClick={() => {
                                      handlePartnerKeyChange(partner.partnerKey);
                                    }}
                                  >
                                    <Box>
                                      <Typography variant="h6" className="count"  >
                                        {partner.pendingCount}
                                      </Typography>
                                    </Box>
                                    <Box>
                                      <Typography variant="caption" className={globalStyles.toggleFontColor}>
                                        <u>{partner.name}</u>
                                      </Typography>
                                    </Box>
                                  </Box>

                                </Grid>
                              ))}
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={6} lg={12}>
                      <Card elevation={0} className={globalStyles.rightCardContainer}>
                        <CardHeader title="" subheader="ENROLLMENT DASHBOARD" />
                        <CardContent>
                          <HighchartsReact highcharts={Highcharts} options={chartOptions} />
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Grid>
                : null
            }
          </Grid>

          <Dialog open={openBatchReviewDialog}
            className={globalStyles.dialogContainer}
            maxWidth="xl"
            fullWidth>
            <DialogTitle className={globalStyles.dialogTitleContainer}>
              Batch Location Review
            </DialogTitle>
            <DialogContent dividers style={{ maxHeight: '95vh', height: '90vh' }}>
              <Grid container spacing={3}>
                <Grid item xs={12} >
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Search"
                        value={reviewLocationSearch}
                        variant="outlined"
                        onChange={(e) => {
                          setReviewLocationSearch(e.target.value);
                          filterBatchReviewLocations(e.target.value, enlistmentPendingReviewLocations);
                        }}
                        onPaste={(e) => {
                          e.preventDefault();
                          try {
                            const pastedText = e.clipboardData.getData('text');
                            const terms = pastedText
                              .replace(/[\r\n]+/g, '|')
                              .split('|')
                              .map(t => t.trim().toLowerCase())
                              .filter(t => t.length > 0);
                            const uniqueTerms = Array.from(new Set(terms)).join(' | ');

                            const target = e.target as HTMLInputElement;
                            const newValue =
                              target.value.slice(0, target.selectionStart!) +
                              uniqueTerms +
                              target.value.slice(target.selectionEnd!);

                            target.value = newValue;
                            setReviewLocationSearch(uniqueTerms);
                            filterBatchReviewLocations(uniqueTerms, enlistmentPendingReviewLocations);
                          }
                          catch { }
                        }}
                        helperText="Search multiple terms separated by | (e.g., AC-001 | NY | 7886CECC-1115-42F9-B6D9-30E8F4B85DD7).When you paste location keys, each key will be auto-separated with |."
                      />
                    </Grid>
                    <Grid item xs={12} md={6} lg={4} >
                      <FormControl fullWidth margin="normal">
                        <InputLabel>Status</InputLabel>
                        <Select
                          value={batchReviewStatus}
                          label="Status"
                          onChange={(e) => setBatchReviewStatus(Number(e.target.value))}
                        >
                          <MenuItem value={1}>Approve</MenuItem>
                          <MenuItem value={2}>Withdraw</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6} lg={8}>
                      <Box display="flex" justifyContent="flex-end" alignItems="center" height="100%">
                        {
                          selectedBatchReviewLocationKeys.length > 0 && <span>{selectedBatchReviewLocationKeys.length} rows selected&nbsp; &nbsp; &nbsp; &nbsp;</span>
                        }
                        {batchEnlistmentReviewLocations.length}&nbsp;of&nbsp;
                        {getEnlistmentBatchReviewLocationCount()} Locations
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>

              {
                batchEnlistmentReviewLocations.length > 0 &&

                <Box style={{ minHeight: '450px', maxHeight: '50vh !important', height: 'calc(100vh - 450px)' }}  >
                  <Box p={0} className={globalStyles.datagridRoot}>
                    <DataGrid
                      rows={batchEnlistmentReviewLocations}
                      columns={columnsForBatchReview}
                      pageSize={batchReviewPageSize}
                      rowsPerPageOptions={[25, 50, 100]}
                      onPageSizeChange={(newPageSize) => setBatchReviewPageSize(newPageSize)}
                      checkboxSelection
                      disableSelectionOnClick
                      autoHeight={false}
                      density={"standard"}
                      hideFooter={false}
                      disableColumnFilter
                      components={{ Toolbar: CustomToolbar }}
                      selectionModel={selectedBatchReviewLocationKeys}
                      onSelectionModelChange={(newSelection) => {
                        setSelectedBatchReviewLocationKeys(newSelection as string[]);
                      }}

                    />
                  </Box>
                </Box>
              }
            </DialogContent>

            <DialogActions>

              {
                !dataImportedFirstTime && dataImported &&
                <Button variant="contained" onClick={() => {
                  handleReviewFileImportCancel();
                }}>
                  Cancel Import
                </Button>
              }

              {
                !dataImported &&
                <><Button variant="contained" onClick={() => {
                  setOpenImportReviewDataDialog(true);
                  setTimeout(() => {
                    fileInputRef.current?.click();
                  }, 100);
                }}>
                  Import
                </Button>
                  <Button variant="contained" onClick={() => handleReviewFileExport()}>
                    Export
                  </Button>
                </>
              }

              <Button onClick={() => setOpenBatchReviewDialog(false)} className={globalStyles.buttonDefault}>Cancel</Button>
              <Button
                disabled={selectedBatchReviewLocationKeys.length === 0}
                variant="contained"
                color="secondary"
                className={globalStyles.buttonDefault}
                onClick={() => {
                  if (validateBatchReviewLocations()) {
                    setBatchReviewConfirmDialogOpen(true);
                  }
                }}
              >
                Confirm
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog open={openImportReviewDataDialog} maxWidth="sm" fullWidth>
            <DialogContent dividers>

              <input
                type="file"
                accept=".xlsx, .xls"
                hidden
                ref={fileInputRef}
                onChange={handleReviewFileImport}
              />

              <Box p={3} textAlign="center">
                <Typography variant="h6" gutterBottom>
                  Select Enlistment Review File
                </Typography>
                <Box p={2}>
                  <Button variant="outlined" onClick={() => fileInputRef.current?.click()}>
                    Browse
                  </Button>
                </Box>
              </Box>

              <Box p={3} textAlign="center">
                <Typography variant="body2" gutterBottom>
                  The sheet name must be called <b>"data"</b>.<br />
                  Expected Columns: <b>key</b>, <b>accountnumber</b>
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenImportReviewDataDialog(false)}>Cancel</Button>
            </DialogActions>
          </Dialog>
        </Screen>
      )}
    </React.Fragment>
  );
};

const LocalPageStyles = makeStyles((theme: Theme) =>
  createStyles({
    datagridContainer: {
      minHeight: "350px",
      height: "calc(100vh - 350px)",
      width: "100%",
      marginBottom: theme.spacing(2),

      [theme.breakpoints.down("sm")]: {
        height: "450px",
      },
    },
    ellipsisCell: {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxWidth: '250px',
    },
  })
);

export default Enlistment;
